import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { observer } from "mobx-react";
import { useStore, useDebounce } from "../../../../../hooks";
import { useMediaQueries } from "../../../../../utils/mediaQueries";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { API } from "aws-amplify";
import { putDocumentFileUpload } from "../../../../../graphql/queries";
import {
  StyledContainer,
  TableWrapper,
  BreadCrumbContainer,
  PageContainer,
  SearchAndButtonContainer,
  SearchContainer,
  CustomSearchInput,
  InputField,
  SearchIcon,
  ButtonWrapper,
  FilterWrapper,
  DragDropOverlay,
  TableContainer,
  UploadButtonContent,
  ActionsCellContainer,
  SharedAgentsBadge,
} from "./DocumentVault.styled";
import { getDensity } from "../../../../../utils/GetDensity/GetDensity";
import DynamicBreadcrumbs from "../../../../../elements/v2/DynamicBreadcrumbs/DynamicBreadcrumbs";
import { generateColumnTable } from "../../../../../utils/tableUtils";
import { Icon, RoundedButton } from "../../../../../elements";
import Alerts from "../../../../../elements/v2/Alerts/Alerts";
import SmartTableContainer from "../../../../../elements/v2/SmartTableContainer/SmartTableContainer";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import Document from "../../../VaultPage/components/Document/Document";
import DocumentContentModal from "../../../VaultPage/components/DocumentContentModal/DocumentContentModal";
import FilterSelector from "../../../../../elements/v2/FilterSelector/FilterSelector";
import { commonFilterProps } from "../../../AsistantManagement/consts";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { vaultStore } from "../../store/VaultStore";
import DragAndDrop from "../../components/DragAndDrop/DragAndDrop";
import { FileTypeFilterValues } from "../../enums/FileTypeFilterEnum";
import {
  StatusFilterValues,
  StatusFilterEnum,
} from "../../enums/StatusFilterEnum";
import timeAgo from "../../../../../utils/timeAgo";
import { DISPLAY_MODE } from "../../../../../components/app/consts";
import { statusColumn } from "../../components/VaultBadgeStatus/VaultBadgeStatus";
import MenuButtonWrapper from "../../../../../elements/v2/MenuButtonWrapper";
import ShareVaultFileModal from "../../components/ShareVaultFileModal/ShareVaultFileModal";
import ArchiveVaultFileModal from "../../components/ArchiveVaultFileModal/ArchiveVaultFileModal";
import RenameVaultFileModal from "../../components/RenameVaultFileModal/RenameVaultFileModal";

const DocumentVaultPage = observer(() => {
  const { authStore } = useStore();
  const { isMobile, isTablet, isDesktop } = useMediaQueries();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const fullSearch = location.search + location.hash;
  const urlParams = new URLSearchParams(fullSearch.replace("#", "%23"));
  const folderId = decodeURIComponent(urlParams.get("id") || "");
  const customerId = urlParams.get("customerId");

  const modeTable = searchParams.get('mode') || DISPLAY_MODE.TABLE;

  const vaultName = () => {
    const name = searchParams.get("vaultName");
    return name ? decodeURIComponent(name) : "-";
  };

  const [, setGlobalFilter] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const [sorting, setSorting] = useState([{ id: "contact_name", desc: false }]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 15,
  });
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const density = useMemo(
    () => getDensity(isMobile, isTablet, isDesktop),
    [isMobile, isTablet, isDesktop]
  );
  const [fileTypeFilter, setFileTypeFilter] = useState([]);
  const [uploadedByFilter, setUploadedByFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [latestUpdatedFilter, setLatestUpdatedFilter] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showUploadView, setShowUploadView] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [sortedFiles, setSortedFiles] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);

  const uploadedByOptions = useMemo(() => {
    if (!authStore.users) return [];
    return authStore.users.map((user) => ({
      value: user.id,
      label: `${user.first_name} ${user.last_name || ""}`.trim(),
    }));
  }, [authStore.users]);

  useEffect(() => {
    if (folderId && customerId) {
      void vaultStore.fetchDocumentFiles(
        folderId,
        customerId,
        debouncedSearchValue,
        statusFilter[0],
        uploadedByFilter[0] || ""
      ).finally(() => {
        setIsInitialLoading(false);
      });
    }
  }, [
    folderId,
    customerId,
    debouncedSearchValue,
    fileTypeFilter,
    uploadedByFilter,
    statusFilter,
    latestUpdatedFilter,
  ]);

  useEffect(() => {
    if (vaultStore.files) {
      setSortedFiles(
        [...vaultStore.files].sort((a, b) => {
          if (
            a.status === StatusFilterEnum.ARCHIVED &&
            b.status !== StatusFilterEnum.ARCHIVED
          )
            return 1;
          if (
            a.status !== StatusFilterEnum.ARCHIVED &&
            b.status === StatusFilterEnum.ARCHIVED
          )
            return -1;
          return 0;
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vaultStore.files]);

  useEffect(() => {
    setGlobalFilter(debouncedSearchValue);
  }, [debouncedSearchValue]);

  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
  }, []);

  const handleFilterChange = useCallback(
    (filterType) => (event) => {
      const newValue =
        typeof event.target.value === "string"
          ? event.target.value.split(",")
          : event.target.value;

      const filterConfigs = {
        fileType: {
          setState: setFileTypeFilter,
        },
        uploadedBy: {
          setState: setUploadedByFilter,
        },
        status: {
          setState: setStatusFilter,
        },
        latestUpdated: {
          setState: setLatestUpdatedFilter,
        },
      };

      const config = filterConfigs[filterType];
      if (config) {
        config.setState(newValue);
      }
    },
    []
  );

  const handleClearAll = useCallback(() => {
    setFileTypeFilter([]);
    setUploadedByFilter([]);
    setStatusFilter([]);
    setLatestUpdatedFilter([]);
  }, []);

  const filterSelectorsConfig = useMemo(
    () => [
      {
        title: "File Type",
        data: FileTypeFilterValues,
        minWidthForm: 120,
        minWidthPaper: 224,
        value: fileTypeFilter,
        filterType: "fileType",
        type: "single",
      },
      {
        title: "Uploaded by",
        data: uploadedByOptions,
        minWidthForm: 140,
        minWidthPaper: 224,
        value: uploadedByFilter,
        filterType: "uploadedBy",
        isObjectData: true,
        type: "single",
      },
      {
        title: "Status",
        data: StatusFilterValues,
        minWidthForm: 120,
        minWidthPaper: 224,
        value: statusFilter,
        filterType: "status",
        type: "single",
      },
      {
        title: "Last Updated",
        data: ["Today", "Last 7 days", "Last 30 days", "Last 90 days"],
        minWidthForm: 160,
        minWidthPaper: 224,
        value: latestUpdatedFilter,
        filterType: "latestUpdated",
        type: "single",
      },
    ],
    [
      fileTypeFilter,
      uploadedByFilter,
      statusFilter,
      latestUpdatedFilter,
      uploadedByOptions,
    ]
  );

  const handleDocumentClick = useCallback((rowData) => {
    setSelectedDocument(rowData);
    setShowDocumentModal(true);
  }, []);

  const getMenuItems = (rowData) => [
    { 
      label: 'Download', 
      onClick: () => {
        console.log('Download clicked');
      }
    },
    { 
      label: 'Rename', 
      onClick: () => {
        setSelectedDocument(rowData);
        setShowRenameModal(true);
      }
    },
    { 
      label: 'Duplicate', 
      onClick: () => {
        console.log('Duplicate clicked');
      }
    },
    { 
      label: 'Manage Access', 
      onClick: () => {
        console.log('Manage Access clicked');
      }
    },
    { 
      label: 'Share', 
      onClick: () => {
        setSelectedDocument(rowData);
        setShowShareModal(true);
      }
    },
    { 
      label: 'Archive File', 
      onClick: () => {
        setSelectedDocument(rowData);
        setShowArchiveModal(true);
      }
    },
    { 
      label: 'View Activity Log', 
      onClick: () => {
        console.log('View Activity Log clicked');
      }
    }
  ];

  const columns = [
    {
      ...generateColumnTable("name", "Name"),
      Cell: ({ cell }) => <span data-testid={`doc-name-${cell.row.index}`}>{cell.getValue()}</span> || "-",
    },
    {
      ...generateColumnTable("description", "Description"),
      Cell: ({ cell }) => cell.getValue() || "-",
    },
    {
      ...generateColumnTable("metadata.shared_with_count", "Link agents"),
      Cell: ({ cell }) => (
        <SharedAgentsBadge>
          <Icon name="agentShare" height="14px" width="14px" /> {cell.getValue() || "0"} {`+`}
        </SharedAgentsBadge>
      ),
    },
    statusColumn,
    {
      ...generateColumnTable("last_modified", "Last Updated"),
      Cell: ({ cell }) => {
        const value = cell.getValue();
        return value ? timeAgo(value) : "-";
      },
    },
    {
      ...generateColumnTable("size", "Size"),
      Cell: ({ cell }) => cell.getValue() + " MB" || "-",
    },
    {
      ...generateColumnTable("actions", "Actions"),
      Cell: ({ row }) => {
        const rowData = row.original;
        return (
          <ActionsCellContainer onClick={(e) => e.stopPropagation()}>
            <MenuButtonWrapper items={getMenuItems(rowData)} />
          </ActionsCellContainer>
        );
      },
      muiTableBodyCellProps: {
        sx: {
          position: 'relative',
          padding: '16px !important',
        },
      },
    },
  ];

  const breadcrumbItems = [
    { label: "Vault", href: `/v2/vault?mode=${modeTable}` },
    { label: vaultName(), href: location.pathname + location.search },
  ];

  const handleBackClick = useCallback(() => {
    const currentMode = searchParams.get('mode') || DISPLAY_MODE.TABLE;
    navigate(`/v2/vault?mode=${currentMode}`);
  }, [navigate, searchParams]);

  const handleUploadToggle = () => {
    setShowUploadView(!showUploadView);
  };

  const handleFileDrop = (files) => {
    console.log("Dropped files:", files);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes("Files")) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileDrop(files);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file && customerId && folderId) {
      setIsUploading(true);
      try {
        const input = {
          customer_id: customerId,
          folder_id: folderId,
          created_by: authStore.userId,
          status: "active",
          name: file.name,
          file_type: file.type || "application/octet-stream",
        };

        const response = await API.graphql({
          query: putDocumentFileUpload,
          variables: { input },
          authMode: "AMAZON_COGNITO_USER_POOLS",
        });

        if (response.data.putDocumentFileUpload.statusCode === "200") {
          const responseBody = JSON.parse(
            response.data.putDocumentFileUpload.body
          );
          const uploadUrl = responseBody.url;

          const uploadResponse = await fetch(uploadUrl, {
            method: "PUT",
            body: file,
            headers: {
              "Content-Type": file.type || "application/octet-stream",
            },
          });

          if (uploadResponse.ok) {
            await vaultStore.fetchDocumentFiles(
              folderId,
              customerId,
              debouncedSearchValue,
              statusFilter[0],
              ""
            );
            await new Promise((resolve) => setTimeout(resolve, 100));
            setShowSuccessAlert(true);
          } else {
            throw new Error("Failed to upload file to S3");
          }
        } else {
          throw new Error("Failed to get upload URL");
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        vaultStore.error = {
          show: true,
          severity: "error",
          title: "Error uploading file",
          message: error.message,
        };
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleUploadSuccess = useCallback(async () => {
    if (folderId && customerId) {
      try {
        await vaultStore.fetchDocumentFiles(
          folderId,
          customerId,
          debouncedSearchValue,
          statusFilter[0],
          ""
        );
        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (error) {
        console.error("Error refreshing files:", error);
      }
    }
  }, [folderId, customerId, debouncedSearchValue, statusFilter]);

  const handleModeTableChange = useCallback((mode) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('mode', mode);
    setSearchParams(newSearchParams);
  }, [searchParams, setSearchParams]);

  const handleRename = () => {
    setShowRenameModal(false);
    if (customerId && folderId) {
      void vaultStore.fetchDocumentFiles(
        folderId,
        customerId,
        debouncedSearchValue,
        statusFilter[0],
        uploadedByFilter[0] || ''
      );
    }
  };

  const handleArchive = () => {
    setShowArchiveModal(false);
    if (customerId && folderId) {
      void vaultStore.fetchDocumentFiles(
        folderId,
        customerId,
        debouncedSearchValue,
        statusFilter[0],
        uploadedByFilter[0] || ''
      );
    }
  };

  return (
    <PageContainer>
      {showSuccessAlert && (
        <Alerts
          severity="success"
          title="Document uploaded successfully"
          message="Your document has been successfully uploaded to the vault."
          onClose={() => setShowSuccessAlert(false)}
        />
      )}

      {vaultStore.error.show && (
        <Alerts
          severity={vaultStore.error.severity}
          title={vaultStore.error.title}
          message={vaultStore.error.message}
          onClose={() => vaultStore.closeAlert()}
        />
      )}

      <BreadCrumbContainer>
        <DynamicBreadcrumbs
          items={breadcrumbItems}
          currentPath={location.pathname + location.search}
        />
      </BreadCrumbContainer>

      <StyledContainer>
        <SearchAndButtonContainer>
          <ArrowBackIcon
            style={{
              cursor: "pointer",
              marginRight: "10px",
              color: "#666",
              display: "flex",
              alignItems: "center",
            }}
            onClick={handleBackClick}
          />
          <SearchContainer>
            <CustomSearchInput>
              <div
                style={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                <SearchIcon>
                  <Icon name="magnifier" height="20px" width="20px" />
                </SearchIcon>
                <InputField
                  type="text"
                  value={searchValue}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search documents..."
                />
              </div>
            </CustomSearchInput>
            <FilterWrapper>
              {filterSelectorsConfig.map((config) => (
                <FilterSelector
                  key={config.title}
                  {...commonFilterProps()}
                  {...config}
                  handleChange={handleFilterChange(config.filterType)}
                />
              ))}
              <RoundedButton
                width="86px"
                height="40px"
                kind="confirm"
                outline={true}
                fontWeight="normal"
                fontSize="14px"
                margin="0"
                borderRadius="8px"
                onClick={handleClearAll}
              >
                Clear all
              </RoundedButton>
            </FilterWrapper>
          </SearchContainer>
          <ButtonWrapper>
            <SmartTableContainer.ModeSwitcher
              mode={modeTable}
              setMode={handleModeTableChange}
            />
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            <RoundedButton
              kind="blue"
              onClick={showUploadView ? handleUploadToggle : triggerFileInput}
              disabled={isUploading}
              height="40px"
            >
              <UploadButtonContent>
                {isUploading ? (
                  "Uploading..."
                ) : (
                <>
                  {showUploadView ? <CloseIcon /> : <AddIcon />}
                  {showUploadView ? "Close" : "Upload"}
                </>
              )}
              </UploadButtonContent>
            </RoundedButton>
          </ButtonWrapper>
        </SearchAndButtonContainer>

        <TableWrapper>
          <TableContainer
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {isDragging && (
              <DragDropOverlay>
                <DragAndDrop
                  onDrop={handleFileDrop}
                  onClose={() => setIsDragging(false)}
                  onUploadSuccess={handleUploadSuccess}
                  isUploading={isUploading}
                  customerId={customerId}
                  folderId={folderId}
                />
              </DragDropOverlay>
            )}
            <SmartTableContainer
              columns={columns}
              data={sortedFiles}
              rowCount={vaultStore.totalFilesCount}
              pagination={pagination}
              setPagination={setPagination}
              sorting={sorting}
              setSorting={setSorting}
              isLoading={isInitialLoading || vaultStore.isLoadingFiles}
              enableRowSelection={false}
              density={density}
              mode={modeTable}
              itemToRender={<Document onClick={handleDocumentClick} />}
              showEmptyData={
                !isInitialLoading && (
                  <SmartTableContainer.EmptyState
                    title="No documents found"
                    description="You haven't uploaded any documents to this vault yet."
                  />
                )
              }
              columnsGrid={6}
            />
          </TableContainer>
        </TableWrapper>

        <DocumentContentModal
          isOpen={showDocumentModal}
          onClose={() => setShowDocumentModal(false)}
          documentData={selectedDocument}
        />

        <RenameVaultFileModal
          isOpen={showRenameModal}
          onClose={() => setShowRenameModal(false)}
          onSubmit={handleRename}
          currentName={selectedDocument?.name}
          fileId={selectedDocument?.id}
          created_by={selectedDocument?.created_by}
          status={selectedDocument?.status}
        />

        <ArchiveVaultFileModal
          isOpen={showArchiveModal}
          onClose={() => setShowArchiveModal(false)}
          onSubmit={handleArchive}
          name={selectedDocument?.name}
          fileId={selectedDocument?.id}
          status={selectedDocument?.status}
        />

        <ShareVaultFileModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          fileId={selectedDocument?.id}
          vaultName={selectedDocument?.name}
          vaultId={folderId}
          customerId={customerId}
        />
      </StyledContainer>
    </PageContainer>
  );
});

export default DocumentVaultPage;
